// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableRow,
  TableCell,
  Divider,
  DialogTitle,
  Chip,
  TextField,
  MenuItem,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import axios from "axios.js";
import AppConfig from "appConfig";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";

function CallsPage() {
  const pageTitle = "Call Logs";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const statusDialog = useAwaitDialog();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [calls, setCalls] = useState([]);
  const [call, setCall] = useState(null);
  const [callCenterAgentGroups, setCallCenterAgentGroups] = useState([]);
  const [agents, setAgents] = useState([]);
  const [currency, setCurrency] = useState(null);
  const [transcript, setTranscript] = useState(null);

  const [qaTemplates, setQATemplates] = useState([]);
  const [selectedQATemplate, setSelectedQATemplate] = useState([]);
  const [qaReview, setQAReview] = useState(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [isSubmittinReview, setIsSubmittinReview] = useState(false);

  // Advanced Search Panel Configuration States
  const [showFilters, setShowFilters] = useState(false);
  const [searchParams, setSearchParams] = useState({
    group: null,
    agent: null,
    caller: "",
    direction: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setSearchParams({
      agent: "",
      caller: "",
      direction: "",
      status: "",
      startDate: "",
      endDate: "",
    });
    setRefreshPage((prev) => !prev);
  };

  const handleSearchSubmit = (e) => {
    // e.preventDefault();
    setRefreshPage(!refreshPage);
  };

  const tableColumns = [
    { Header: "#", accessor: "direction_icon", align: "left" },
    { Header: "Direction", accessor: "direction", align: "left" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Contact", accessor: "caller", align: "left" },
    { Header: "Duration", accessor: "duration", align: "left" },
    { Header: "Cost", accessor: "charge", align: "left" },
    { Header: "Outcome", accessor: "outcome", align: "left" },
    { Header: "Recording", accessor: "recording", align: "center" },
    { Header: "GPS", accessor: "location", align: "center" },
    { Header: "Date", accessor: "date", align: "right" },
  ];

  const buildRateRows = useCallback((data, crcy) => {
    function getCallIcon(row) {
      if (row.direction === "inbound") {
        return row.status === "completed" ? "phone_callback" : "phone_missed";
      } else {
        return row.status === "completed" ? "phone_forwarded" : "call_end";
      }
    }

    function getCallColor(row) {
      if (row.direction === "inbound") {
        return row.status === "completed" ? "warning" : "error";
      } else {
        return row.status === "completed" ? "success" : "dark";
      }
    }

    setCurrency(crcy);
    const rows = [];
    data.map((row) => {
      rows.push({
        direction_icon: (
          <MDButton
            size="small"
            iconOnly
            variant="outlined"
            color={getCallColor(row)}
            onClick={() => {
              if (row.status !== "completed") return;
              getCall(row.reference);
            }}
          >
            <Icon>{getCallIcon(row)}</Icon>
          </MDButton>
        ),
        direction: row.direction.toUpperCase(),
        status: row.status.toUpperCase(),
        agent: row.agent,
        caller: row.caller ?? "---",
        duration: `${row.duration}s`,
        charge: `${crcy}${(row.charge / 100).toLocaleString("en", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        outcome: row.outcome,
        location: row.latitude ? (
          <a
            rel="noreferrer"
            target="_blank"
            href={`https://maps.google.com?q=${row.latitude},${row.longitude}`}
          >
            <Icon>location_on</Icon>
          </a>
        ) : (
          "---"
        ),
        recording: row.recording ? (
          <MDBox mt={1} variant="button">
            <a href={`${AppConfig.api_media_url}${row.recording}`} target="_blank" rel="noreferrer">
              <Icon>play_circle</Icon>
            </a>
          </MDBox>
        ) : (
          "---"
        ),
        date: row.created,
      });
      return null;
    });

    return rows;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      const queryParams = new URLSearchParams();
      Object.keys(searchParams).forEach((key) => {
        if (searchParams[key]) {
          queryParams.append(key, searchParams[key]);
        }
      });

      const queryString = queryParams.toString();
      const endpoint = `/portal/voice/call_centers/${ref}/calls.json${
        queryString ? `?${queryString}` : ""
      }`;

      await axios
        .get(endpoint)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            console.log(result.data.data);
            setCallCenterAgentGroups(result.data.data.groups);
            setAgents(result.data.data.agents);
            setQATemplates(result.data.data.qa_templates);
            const rows = buildRateRows(result.data.data.calls, result.data.currency);
            setCalls(rows);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage, ref, buildRateRows]);

  async function getCall(callRef) {
    setShowLoading(true);
    await axios
      .get(`portal/voice/call_centers/${callRef}/call.json`)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setCall(result.data.data.call);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function generateAnalysis(callRef) {
    setCall(null);
    setShowLoading(true);
    await axios
      .get(`portal/voice/call_centers/${callRef}/analyse.json`)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function submitQAReview() {
    if (!qaReview) {
      setSeverity("error");
      setErrMsg("Please fill out the review form properly.");
      setOpen(true);
      return;
    }

    setIsSubmittinReview(true);
    const json = {
      reference: call.reference,
      review: qaReview,
    };

    await axios
      .post(`portal/voice/call_centers/qa/reviews/submit.json`, json)
      .then(async (result) => {
        setIsSubmittinReview(false);
        if (result.data.status === "success") {
          setOpenReviewDialog(false);
          await statusDialog.show(result.data.data.message, "success", "Success!");
          setQAReview(null);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setIsSubmittinReview(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openReviewDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>QA Review</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox mt={2}>
            <TextField
              select
              fullWidth
              label="Templates (Score Cards)"
              value={selectedQATemplate || ""} // Added value binding to keep the dropdown controlled
              onChange={(e) => {
                const templatePayload = qaTemplates.find((t) => t.reference === e.target.value);
                let rv = {
                  template: e.target.value,
                  metrics: [],
                };

                if (templatePayload && templatePayload.metrics) {
                  rv.metrics = templatePayload.metrics.map((metric) => ({
                    reference: metric.reference,
                    title: metric.title,
                    score: 0, // Initialized to 0 as per your definition
                  }));
                }

                setQAReview(rv);
                setSelectedQATemplate(e.target.value);
              }}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {qaTemplates.length > 0 &&
                qaTemplates.map((record) => (
                  <MenuItem key={record.reference} value={`${record.reference}`}>
                    {record.title}
                  </MenuItem>
                ))}
            </TextField>
          </MDBox>

          {qaReview && (
            <MDBox mt={3}>
              <MDBox mt={5}>
                {qaReview.metrics.map((metric) => (
                  <MDBox mb={3} key={metric.reference}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={8} lg={8}>
                        <MDTypography variant="body2" pl={1}>
                          {metric.title}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12} md={4} lg={4}>
                        <MDInput
                          fullWidth
                          type="number" // Changed to number for clean score logging
                          label="Score"
                          value={metric.score} // Binds directly to the state array element
                          onChange={(e) => {
                            const val = e.target.value === "" ? "" : Number(e.target.value);

                            // Map through the array and substitute the modified item score value
                            setQAReview((prev) => ({
                              ...prev,
                              metrics: prev.metrics.map((m) =>
                                m.reference === metric.reference ? { ...m, score: val } : m
                              ),
                            }));
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                ))}
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenReviewDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          {isSubmittinReview ? (
            <MDBox px={3}>
              <CircularProgress size={32} color="dark" />
            </MDBox>
          ) : (
            <MDButton
              color="dark"
              variant="contained"
              onClick={() => {
                submitQAReview();
              }}
            >
              Submit Review
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <MDBox pt={0} pb={3}>
        <MDBox mb={1}>
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/voice/call-centers/${ref}`);
            }}
          >
            Back
          </Button>
        </MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {/* Control Header & Search Panel */}
            <Card sx={{ mb: 3, p: 2 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={showFilters ? 3 : 0}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="h5" fontWeight="medium" ml={1}>
                    Call Logs
                  </MDTypography>
                </MDBox>

                <MDButton
                  variant="gradient"
                  color={showFilters ? "dark" : "info"}
                  onClick={() => setShowFilters(!showFilters)}
                  startIcon={<Icon>tune</Icon>}
                >
                  {showFilters ? "Hide Filters" : "Advanced Search"}
                </MDButton>
              </MDBox>

              {showFilters && (
                <component-form component="form" onSubmit={handleSearchSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={6}>
                          <TextField
                            select
                            fullWidth
                            label="Agent Group"
                            name="group"
                            value={searchParams.group}
                            onChange={handleFilterChange}
                            SelectProps={{ style: { padding: "12px 0px" } }}
                          >
                            {
                              // --> Notice below changes
                              callCenterAgentGroups.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                                callCenterAgentGroups.map((record) => (
                                  <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                                ))
                            }
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                          <TextField
                            select
                            fullWidth
                            label="Agents"
                            name="agent"
                            value={searchParams.agent}
                            onChange={handleFilterChange}
                            SelectProps={{ style: { padding: "12px 0px" } }}
                          >
                            {
                              // --> Notice below changes
                              agents.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                                agents.map((record) => (
                                  <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                                ))
                            }
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                          <MDInput
                            fullWidth
                            type="date"
                            label="Start Date"
                            name="startDate"
                            value={searchParams.startDate}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                          <MDInput
                            fullWidth
                            type="date"
                            label="End Date"
                            name="endDate"
                            value={searchParams.endDate}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={6}>
                          <MDInput
                            fullWidth
                            label="Contact Number"
                            name="caller"
                            value={searchParams.caller}
                            onChange={handleFilterChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                          <TextField
                            select
                            fullWidth
                            label="Direction"
                            name="direction"
                            value={searchParams.direction}
                            onChange={handleFilterChange}
                            SelectProps={{ style: { padding: "12px 0px" } }}
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="inbound">Inbound</MenuItem>
                            <MenuItem value="outbound">Outbound</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                          <TextField
                            select
                            fullWidth
                            label="Status"
                            name="status"
                            value={searchParams.status}
                            onChange={handleFilterChange}
                            SelectProps={{ style: { padding: "12px 0px" } }}
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                            <MenuItem value="DROPPED">DROPPED</MenuItem>
                            <MenuItem value="MISSED">MISSED</MenuItem>
                            <MenuItem value="FAILED">FAILED</MenuItem>
                          </TextField>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={6}
                          lg={6}
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          gap={2}
                        >
                          <MDButton variant="outlined" color="dark" onClick={resetFilters}>
                            Reset
                          </MDButton>
                          <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => setRefreshPage(!refreshPage)}
                          >
                            Search
                          </MDButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </component-form>
              )}
            </Card>

            {/* Logs Presentation View */}
            {pageLoaded && (
              <Card>
                <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6">Call Entries</MDTypography>
                  </MDBox>
                  <MDBox>
                    <Tooltip title="Refresh">
                      <MDButton
                        variant="outlined"
                        color="info"
                        iconOnly
                        onClick={() => {
                          setRefreshPage(!refreshPage);
                        }}
                      >
                        <Icon>replay</Icon>
                      </MDButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>
                <MDBox pt={0}>
                  <DataTable
                    table={{ columns: tableColumns, rows: calls }}
                    isSorted={false}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>

      {/* Transcript Dialog View */}
      {transcript && (
        <Dialog open fullWidth maxWidth="md">
          <DialogTitle>Call Transcript</DialogTitle>
          <DialogContent>
            <MDBox
              sx={{
                maxHeight: 500,
                overflowY: "auto",
                px: 1,
                py: 2,
                bgcolor: "#f5f5f5",
                borderRadius: 2,
              }}
            >
              {call?.analysis?.transcript.map((item, index) => {
                const isAgent = item.speaker === "AGENT";
                return (
                  <MDBox
                    key={index}
                    display="flex"
                    justifyContent={isAgent ? "flex-start" : "flex-end"}
                    mb={2}
                  >
                    <MDBox
                      sx={{
                        maxWidth: "75%",
                        px: 2,
                        py: 1.5,
                        borderRadius: 3,
                        bgcolor: isAgent ? "dark.main" : "#ffffff",
                        color: isAgent ? "#fff" : "#000",
                        boxShadow: 1,
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.5}
                      >
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color={isAgent ? "white" : "dark"}
                        >
                          {item.speaker}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{ opacity: 0.7, ml: 2 }}
                          color={isAgent ? "white" : "text"}
                        >
                          {Math.floor(item.timestamp_start)}s
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="body2" color={isAgent ? "white" : "dark"}>
                        {item.text}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              startIcon={<Icon>close</Icon>}
              onClick={() => setTranscript(null)}
            >
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Primary Detail Summary Overlay */}
      {call && (
        <Dialog open={!!call} fullWidth maxWidth="lg">
          <DialogContent>
            <MDBox>
              <MDBox align="center">
                <MDTypography fontSize={36}>
                  <Icon color={call.direction === "inbound" ? "warning" : "success"}>
                    {call.direction === "inbound" ? "phone_callback" : "phone_forwarded"}
                  </Icon>
                </MDTypography>
                <MDTypography variant="caption" fontSize={14}>
                  {call.direction.toUpperCase()}
                </MDTypography>
                <Divider />
              </MDBox>
              <Grid mt={2} container spacing={3}>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <MDBox display="flex" alignItems="center" justifyContent="start" gap={1}>
                      <Icon sx={{ color: "info", fontSize: 24 }}>info</Icon>
                      <MDTypography variant="h6" fontWeight="bold">
                        Call Details
                      </MDTypography>
                    </MDBox>
                    <Chip
                      label={call.outcome ?? "---"}
                      sx={{ backgroundColor: "info.main", color: "#fff", fontWeight: "regular" }}
                    />
                  </MDBox>
                  <MDBox mt={3}>
                    <Table>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Contact
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {call.caller}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Agent
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {call.agent}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Duration
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography
                            variant="body2"
                            fontSize={14}
                          >{`${call.duration}s`}</MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Cost
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {`${currency}${(call.charge / 100).toLocaleString("en", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            GPS
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            <a
                              rel="noreferrer"
                              target="_blank"
                              href={`https://maps.google.com?q=${call.latitude},${call.longitude}`}
                            >
                              <Icon>location_on</Icon>
                            </a>
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular">
                            Time
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="body2" fontSize={14}>
                            {call.created}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    </Table>
                  </MDBox>
                  <MDBox mt={5} align="center">
                    {call.recording && (
                      <MDBox>
                        <MDTypography variant="h6">Call Recording</MDTypography>
                        <MDBox mt={3}>
                          <audio src={`${AppConfig.api_media_url}${call.recording}`} controls>
                            Your browser does not support the audio element.
                          </audio>
                        </MDBox>
                      </MDBox>
                    )}
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={6} lg={6}>
                  {call.analysis ? (
                    <MDBox>
                      <MDBox>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1}
                        >
                          <MDBox display="flex" alignItems="center" justifyContent="start" gap={1}>
                            <Icon sx={{ color: "#b455ff", fontSize: 24 }}>auto_awesome</Icon>
                            <MDTypography variant="h6" fontWeight="bold">
                              AI Call Analysis
                            </MDTypography>
                          </MDBox>
                          <Chip
                            label="AI-Assist"
                            sx={{
                              backgroundColor: "primary.main",
                              color: "#fff",
                              fontWeight: "regular",
                            }}
                          />
                        </MDBox>

                        <MDBox p={1} borderRadius={3} textAlign="center">
                          <MDBox
                            width={30}
                            height={30}
                            mx="auto"
                            mb={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius="50%"
                            sx={{
                              backgroundColor:
                                call.analysis.sentiment.sentiment === "angry"
                                  ? "#ef4444"
                                  : call.analysis.sentiment.sentiment === "frustrated"
                                  ? "#f97316"
                                  : call.analysis.sentiment.sentiment === "confused"
                                  ? "#eab308"
                                  : call.analysis.sentiment.sentiment === "neutral"
                                  ? "#9ca3af"
                                  : "#22c55e",
                              color: "#fff",
                            }}
                          >
                            <Icon>
                              {call.analysis.sentiment.sentiment === "angry"
                                ? "sentiment_very_dissatisfied"
                                : call.analysis.sentiment.sentiment === "frustrated"
                                ? "mood_bad"
                                : call.analysis.sentiment.sentiment === "confused"
                                ? "sentiment_neutral"
                                : call.analysis.sentiment.sentiment === "neutral"
                                ? "sentiment_neutral"
                                : "sentiment_satisfied"}
                            </Icon>
                          </MDBox>

                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            fontSize={16}
                            sx={{
                              color:
                                call.analysis.sentiment.sentiment === "angry"
                                  ? "#b91c1c"
                                  : call.analysis.sentiment.sentiment === "frustrated"
                                  ? "#c2410c"
                                  : call.analysis.sentiment.sentiment === "confused"
                                  ? "#a16207"
                                  : "#4b5563",
                            }}
                          >
                            {call.analysis.sentiment.sentiment.toUpperCase()}
                          </MDTypography>
                          <MDBox>
                            <MDTypography variant="caption" fontSize={11} color="text">
                              Contact Sentiment
                            </MDTypography>
                          </MDBox>
                        </MDBox>

                        <Grid mt={1} container spacing={2}>
                          <Grid item xs={6}>
                            <MDBox display="flex" justifyContent="center" alignItems="center">
                              <MDBox position="relative" display="inline-flex">
                                <CircularProgress
                                  variant="determinate"
                                  value={100}
                                  size={100}
                                  thickness={3.6}
                                  sx={{ color: "#e5e7eb", position: "absolute", left: 0 }}
                                />
                                <CircularProgress
                                  variant="determinate"
                                  value={call.analysis.scoring.score}
                                  size={100}
                                  thickness={3.6}
                                  sx={{ color: "#163d7a" }}
                                />
                                <MDBox
                                  top={0}
                                  left={0}
                                  bottom={0}
                                  right={0}
                                  position="absolute"
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <MDTypography variant="h3" fontWeight="bold" fontSize={20}>
                                    {call.analysis.scoring.score.toFixed(0)}%
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text" fontSize={11}>
                                    Overall
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </MDBox>
                          </Grid>

                          <Grid item xs={6}>
                            <Card
                              sx={{
                                backgroundColor: "#ede9fe",
                                borderRadius: 3,
                                boxShadow: "none",
                                height: "100%",
                              }}
                            >
                              <MDBox
                                p={1}
                                textAlign="center"
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                              >
                                <Icon sx={{ color: "#7c3aed", mb: 1, fontSize: 22 }}>
                                  workspace_premium
                                </Icon>
                                <MDTypography
                                  variant="button"
                                  fontWeight="bold"
                                  fontSize={14}
                                  sx={{ color: "#7c3aed" }}
                                >
                                  Professionalism
                                </MDTypography>
                                <MDTypography variant="h4" fontWeight="bold" mt={0.5} fontSize={24}>
                                  {call.analysis.scoring.professionalism.toFixed(0)}
                                </MDTypography>
                              </MDBox>
                            </Card>
                          </Grid>

                          {[
                            {
                              label: "Control",
                              value: call.analysis.scoring.control,
                              color: "#e9f8ee",
                              text: "#16a34a",
                              icon: "support_agent",
                            },
                            {
                              label: "Clarity",
                              value: call.analysis.scoring.clarity,
                              color: "#e8f0ff",
                              text: "#2563eb",
                              icon: "record_voice_over",
                            },
                            {
                              label: "Empathy",
                              value: call.analysis.scoring.empathy,
                              color: "#fce7ef",
                              text: "#e11d48",
                              icon: "favorite",
                            },
                            {
                              label: "Listening",
                              value: call.analysis.scoring.listening,
                              color: "#fdf4d7",
                              text: "#d97706",
                              icon: "hearing",
                            },
                          ].map((item) => (
                            <Grid item xs={6} key={item.label}>
                              <Card
                                sx={{
                                  backgroundColor: item.color,
                                  borderRadius: 3,
                                  boxShadow: "none",
                                  height: "100%",
                                }}
                              >
                                <MDBox
                                  p={1}
                                  textAlign="center"
                                  display="flex"
                                  flexDirection="column"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <Icon sx={{ color: item.text, mb: 0.5, fontSize: 22 }}>
                                    {item.icon}
                                  </Icon>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    fontSize={13}
                                    sx={{ color: item.text }}
                                  >
                                    {item.label}
                                  </MDTypography>
                                  <MDTypography
                                    variant="h4"
                                    fontWeight="bold"
                                    mt={0.5}
                                    fontSize={24}
                                  >
                                    {item.value.toFixed(0)}
                                  </MDTypography>
                                </MDBox>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                      <MDBox mt={5}>
                        <MDButton
                          fullWidth
                          color="dark"
                          onClick={() => setTranscript(call.analysis.transcript)}
                          endIcon={<Icon>forum</Icon>}
                        >
                          View Transcript
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox align="center">
                      <MDTypography fontSize={36}>
                        <Icon>info</Icon>
                      </MDTypography>
                      <MDTypography>
                        {call.recording ? "No call analysis available" : "Call was not recorded"}
                      </MDTypography>
                      {call.recording && (
                        <MDBox mt={5}>
                          <MDButton
                            color="dark"
                            onClick={() => generateAnalysis(call.reference)}
                            endIcon={<Icon>settings_suggestions</Icon>}
                          >
                            Generate Analysis
                          </MDButton>
                        </MDBox>
                      )}
                    </MDBox>
                  )}
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="contained"
              color="info"
              startIcon={<Icon>grade</Icon>}
              onClick={() => {
                setQAReview(null);
                setSelectedQATemplate(null);
                setIsSubmittinReview(false);
                setOpenReviewDialog(true);
              }}
            >
              QA Review
            </MDButton>
            <MDButton
              variant="outlined"
              color="dark"
              startIcon={<Icon>close</Icon>}
              onClick={() => setCall(null)}
            >
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default CallsPage;
